<!-- @format -->

<template>
	<!-- disabled属性为false时不传给组件，否则element-plus还是会对样式做调整 -->
	<component
		:is="getWidgetName(type, name)"
		v-if="disabled"
		v-model="value"
		v-bind="bindPcProps[0]"
		:pc-props="bindPcProps[1]"
		:item="item"
		:disabled="disabled"
		:loading="loading"
		@click="onClick"
		@dblclick="onDblClick"
		@blur="onBlurClick"
		@focus="onFoucsClick"
	/>
	<component
		:is="getWidgetName(type, name)"
		v-else
		v-model="value"
		v-bind="bindPcProps[0]"
		:pc-props="bindPcProps[1]"
		:item="item"
		:loading="loading"
		@click="onClick"
		@dblclick="onDblClick"
		@blur="onBlurClick"
		@focus="onFoucsClick"
	/>
</template>

<script setup lang="ts">
	import { getWidgetName } from '../widget-field';
	import { useBaseConfigStoreWithOut } from '../../config/widget';
	import Script, { useDebounceFn } from '../../utils/script';
	import type { FieldsType, WidgetConfigType } from '../../types';
	import type { Ref } from 'vue';

	interface Props {
		type: string;
		pcProps: Partial<WidgetConfigType>;
		item?: FieldsType;
		modelValue: any;
		disabled?: boolean;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emit = defineEmits(['update:modelValue']);

	const scriptConfig: Script | undefined = inject('scriptConfig', undefined);

	const formDisabled: Ref<boolean> = inject('formDisabled', ref(false));

	const formAttribute: Record<string, any> = inject('formAttribute', {});

	const name = inject<string>('name');

	const fieldActionConfig = inject<Record<string, Function>>('fieldActionConfig', {});

	const baseConfigStore = useBaseConfigStoreWithOut(name);

	const loading = ref<boolean>(false);

	const value = computed({
		get() {
			return props.modelValue;
		},
		set(val) {
			emit('update:modelValue', val);
		},
	});

	const bindPcProps = computed(() => {
		// pcProps[0]时直接绑定到组件上的属性，options[1]是传入options使用
		const pcProps: [Partial<WidgetConfigType>, Partial<WidgetConfigType>] = [{}, {}];
		for (const [key, value] of Object.entries(props.pcProps)) {
			if (baseConfigStore.getFieldsBindpcProps().includes(key)) {
				pcProps[0][key] = value;
			} else {
				pcProps[1][key] = value;
			}
		}
		formAttribute.showDisabledText && (pcProps[1]['showDisabledText'] = formAttribute.showDisabledText);
		return pcProps;
	});

	const disabled = computed(() => {
		return props.pcProps.isRequired === '0' || formDisabled.value || props.disabled;
	});

	const onChange = useDebounceFn(() => {
		if (props.pcProps.onChangeScript) {
			if (fieldActionConfig['onChangeScript']) {
				fieldActionConfig['onChangeScript'](props.item);
			} else if (scriptConfig) {
				(scriptConfig as Script).runScript(
					props.pcProps.code as string,
					'onChangeScript',
					props.pcProps.onChangeScript,
				);
			}
		}
	});

	const onClick = () => {
		if (props.pcProps.onClickScript) {
			if (fieldActionConfig['onClickScript']) {
				fieldActionConfig['onClickScript'](props.item);
			} else if (scriptConfig) {
				loading.value = true;
				(scriptConfig as Script)
					.runScript(props.pcProps.code as string, 'onClickScript', props.pcProps.onClickScript)
					.finally(() => {
						loading.value = false;
					});
			}
		}
	};

	const onDblClick = () => {
		if (props.pcProps.onDoubleClickScript) {
			if (fieldActionConfig['onDoubleClickScript']) {
				fieldActionConfig['onDoubleClickScript'](props.item);
			} else if (props.pcProps.onDoubleClickScript && scriptConfig) {
				loading.value = true;
				(scriptConfig as Script)
					.runScript(props.pcProps.code as string, 'onDoubleClickScript', props.pcProps.onDoubleClickScript)
					.finally(() => {
						loading.value = false;
					});
			}
		}
	};

	const onBlurClick = () => {
		if (props.pcProps.onBlurScript) {
			if (fieldActionConfig['onBlurScript']) {
				fieldActionConfig['onBlurScript'](props.item);
			} else if (props.pcProps.onBlurScript && scriptConfig) {
				loading.value = true;
				(scriptConfig as Script)
					.runScript(props.pcProps.code as string, 'onBlurScript', props.pcProps.onBlurScript)
					.finally(() => {
						loading.value = false;
					});
			}
		}
	};

	const onFoucsClick = () => {
		if (props.pcProps.onFoucsScript) {
			if (fieldActionConfig['onFoucsScript']) {
				fieldActionConfig['onFoucsScript'](props.item);
			} else if (props.pcProps.onFoucsScript && scriptConfig) {
				loading.value = true;
				(scriptConfig as Script)
					.runScript(props.pcProps.code as string, 'onFoucsScript', props.pcProps.onFoucsScript)
					.finally(() => {
						loading.value = false;
					});
			}
		}
	};

	watch(
		value,
		() => {
			onChange();
		},
		{
			deep: true,
		},
	);
</script>

<style scoped lang="scss"></style>
